import type { RefObject } from 'react'
import { Async } from '../../../platform/async/Async'
import { Skeleton } from '../../../platform/async/Skeleton'
import { Overlay } from '../../../platform/overlay/Overlay'
import { Button } from '../../../platform/primitives/Button'
import { Money } from '../../../platform/primitives/Money'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { shortDate } from '../../transactions/txnDisplay'
import { CategoryBrowser } from './CategoryBrowser'
import { usePickerModel } from './usePickerModel'

export type PickerContext = { merchant: string; date: string; amountCents: number; currency: string }

/**
 * The category picker as a dialog — a sheet on a phone (`placement="sheet"`),
 * wide on larger screens. The transaction it is for sits at the top, so the
 * user never picks blind (the iOS sheet's `TransactionContext`).
 *
 * Callers that are ALREADY inside an Overlay must not use this — that would
 * stack a second modal. The bulk dialog embeds `CategoryBrowserLoader` instead.
 *
 * **The sheet pins its footer.** On a phone the root carries its own
 * `category-dialog--sheet` (the same test Overlay resolves `placement="sheet"`
 * with: `useBreakpoint() === 'phone'`), never Overlay's internal panel class.
 * The column runs root → Async's busy wrapper (`category-dialog__browser`) →
 * browser → tiles, every level a shrinkable flex item, so the tiles scroll and
 * Cancel stays at the bottom however many tiles a group has.
 */
export function CategoryPickerDialog({
  open,
  onClose,
  value,
  onPick,
  context,
  allowNone = false,
  restoreFocusTo,
}: {
  open: boolean
  onClose: () => void
  value: string | null
  onPick: (id: string | null) => void
  context?: PickerContext
  allowNone?: boolean
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  const sheet = useBreakpoint() === 'phone'
  if (!open) return null
  const pick = (id: string | null) => {
    onPick(id)
    onClose()
  }
  return (
    <Overlay open onClose={onClose} label="Choose a category" placement="sheet" size="wide" restoreFocusTo={restoreFocusTo}>
      <div className={sheet ? 'dialog category-dialog category-dialog--sheet' : 'dialog category-dialog'}>
        <h2 className="dialog__title">Choose a category</h2>
        {context !== undefined && (
          <p className="category-dialog__context">
            <span>{`${context.merchant} · ${shortDate(context.date)}`}</span>
            <Money cents={context.amountCents} currency={context.currency} kind="flow" />
          </p>
        )}
        <CategoryBrowserLoader value={value} onPick={pick} autoFocus className="category-dialog__browser" />
        <div className="dialog__actions">
          {allowNone && value !== null && (
            <Button type="button" size="page" variant="outline" onClick={() => pick(null)}>
              Remove category
            </Button>
          )}
          <Button type="button" size="page" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Overlay>
  )
}

/** The browser with its own data — for a caller already inside a dialog (bulk). */
export function CategoryBrowserLoader({
  value,
  onPick,
  autoFocus,
  className,
}: {
  value: string | null
  onPick: (id: string) => void
  autoFocus?: boolean
  /** For Async's busy wrapper, which is the box a flex parent lays out. */
  className?: string
}) {
  const model = usePickerModel()
  return (
    <Async surface="categories" state={model} className={className} skeleton={<Skeleton height={320} />} // Required by Async's type; unreachable — useCategories never yields `empty`.
      empty={{ title: 'No categories yet' }}>
      {(m) => <CategoryBrowser model={m} value={value} onPick={onPick} autoFocus={autoFocus} />}
    </Async>
  )
}
